// Utility functions for handling GraphQL responses
// Moved here to avoid circular dependencies between @jira/issue__fetch-services and @jira/platform__services-issue

const isIgnoredError = (error: unknown): boolean => {
	if (typeof error !== 'object') {
		return false;
	}

	if (error === null) {
		return false;
	}

	if (
		'message' in error &&
		error.message === 'Application link is missing or not configured properly'
	) {
		return true;
	}

	if (
		'message' in error &&
		typeof error.message === 'string' &&
		error.message.startsWith('Remote link has globalId with invalid format:')
	) {
		return true;
	}

	return false;
};

const filterOutIgnoredErrors: <T>(
	graphQlResponse: {
		errors?: unknown[];
		data?: T;
	},
	isIgnoredErrorFn: (error: unknown) => boolean,
) => {
	data?: T;
} = <T,>(
	graphQlResponse: { errors?: unknown[]; data?: T },
	isIgnoredErrorFn: (error: unknown) => boolean,
) => {
	if (!graphQlResponse.errors || !Array.isArray(graphQlResponse.errors)) {
		return graphQlResponse;
	}

	const nonIgnoredErrors = graphQlResponse.errors.filter(
		(error: unknown) => !isIgnoredErrorFn(error),
	);
	if (nonIgnoredErrors.length > 0) {
		return {
			...graphQlResponse,
			errors: nonIgnoredErrors,
		};
	}
	const { errors, ...responseWithErrorsRemoved } = graphQlResponse;
	return responseWithErrorsRemoved;
};

const getGraphQlData = <T,>(
	logLocation: string,
	graphQlResponse: { errors?: unknown[]; data?: T },
): T | undefined => {
	if (graphQlResponse.errors) {
		const firstError = graphQlResponse.errors[0];
		// Create a simple error - callers can wrap with ValidationError if needed
		const validationError = new Error(
			typeof firstError === 'object' && firstError && 'message' in firstError
				? String(firstError.message)
				: 'Unknown GraphQL error',
		);
		// Note: Original implementation in @jira/platform__services-issue used log.safeErrorWithoutCustomerData
		// Callers should handle logging as needed to avoid circular dependencies
		throw validationError;
	}
	return graphQlResponse.data;
};

export const graphqlErrorHandling: {
	isIgnoredError: (error: unknown) => boolean;
	filterOutIgnoredErrors: <T>(
		graphQlResponse: { errors?: unknown[]; data?: T },
		isIgnoredErrorFn: (error: unknown) => boolean,
	) => {
		data?: T;
	};
	getGraphQlData: <T>(
		logLocation: string,
		graphQlResponse: { errors?: unknown[]; data?: T },
	) => T | undefined;
} = {
	isIgnoredError,
	filterOutIgnoredErrors,
	getGraphQlData,
};
